import React, { lazy, Suspense, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, FileText, AlertTriangle, CheckCircle2, Clock, Send, ClipboardList, ClipboardCheck, User, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CATEGORY_LABELS, formatDate, getExpirationStatus } from "@/lib/utils-app";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy: recharts é pesado (~200KB) — só carrega quando o dashboard monta.
const CategoryChart = lazy(() => import("@/components/CategoryChart"));
const RemindersCard = lazy(() => import("@/components/RemindersCard"));

const STATUS_COLORS = {
  valido: "#10B981",
  vence_em_breve: "#F59E0B",
  vencido: "#EF4444",
  sem_vencimento: "#94A3B8",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [inspections, setInspections] = useState(null);
  const [loadingInspections, setLoadingInspections] = useState(true);

  const handleSendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send-now", {});
      toast.success(`Email enviado para ${data.recipient || (data.sent && data.sent[0]?.recipients?.join(', ')) || 'destinatários'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar email");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
    api.get("/checklists/today-status")
      .then((r) => setInspections(r.data))
      .catch(() => setInspections({ done: [], pending: [], done_count: 0, pending_count: 0, total_employees: 0, date_brt: "" }))
      .finally(() => setLoadingInspections(false));
  }, []);

  const metrics = [
    {
      key: "employees",
      label: "Colaboradores Ativos",
      value: stats?.active_employees ?? 0,
      sub: `${stats?.total_employees ?? 0} no total`,
      icon: Users,
      iconBg: "bg-brand-900 text-white",
    },
    {
      key: "valid",
      label: "Documentos Válidos",
      value: stats?.stats_by_status?.valido ?? 0,
      sub: `${stats?.total_documents ?? 0} documentos`,
      icon: CheckCircle2,
      iconBg: "bg-emerald-50 text-emerald-700",
    },
    {
      key: "soon",
      label: "Vence em breve",
      value: stats?.stats_by_status?.vence_em_breve ?? 0,
      sub: "Próximos 30 dias",
      icon: Clock,
      iconBg: "bg-amber-50 text-amber-700",
    },
    {
      key: "expired",
      label: "Vencidos",
      value: stats?.stats_by_status?.vencido ?? 0,
      sub: "Requer atenção",
      icon: AlertTriangle,
      iconBg: "bg-red-50 text-red-700",
    },
    {
      key: "pending",
      label: "Documentos Pendentes",
      value: stats?.by_doc_status?.pendente ?? 0,
      sub: "Aguardando ação",
      icon: ClipboardList,
      iconBg: "bg-amber-50 text-amber-700",
    },
  ];

  const CHART_PALETTE = ["#2E1C12", "#5C3A21", "#8B5A2B", "#B07E45", "#EA7E1D", "#D86A1F", "#F39E48", "#7A4B2E", "#43291A", "#6E4422", "#FFDDB6", "#CFA56F"];
  const chartData = stats
    ? Object.entries(stats.by_category)
        .filter(([, v]) => (v || 0) > 0)
        .map(([k, v], i) => ({ name: CATEGORY_LABELS[k] || k, value: v, fill: CHART_PALETTE[i % CHART_PALETTE.length] }))
    : [];

  const statusData = stats ? [
    { name: "Válidos", value: stats.stats_by_status.valido, fill: STATUS_COLORS.valido },
    { name: "Vence em breve", value: stats.stats_by_status.vence_em_breve, fill: STATUS_COLORS.vence_em_breve },
    { name: "Vencidos", value: stats.stats_by_status.vencido, fill: STATUS_COLORS.vencido },
  ] : [];

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <header className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500 mb-1">Visão Geral</p>
          <h1 className="text-3xl md:text-4xl font-outfit font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-2">Acompanhe seus colaboradores e a saúde dos documentos.</p>
        </div>
        <Button
          onClick={handleSendNow}
          disabled={sending}
          data-testid="send-notification-now-button"
          className="bg-brand-900 hover:bg-brand-800 text-white h-10"
        >
          <Send className="w-4 h-4 mr-2" />
          {sending ? "Enviando..." : "Enviar resumo por email"}
        </Button>
      </header>

      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {metrics.map((m, idx) => (
          <Card
            key={m.key}
            data-testid={`metric-card-${m.key}`}
            className="p-5 border-slate-200 shadow-none animate-fade-in-up"
            style={{ animationDelay: `${idx * 70}ms` }}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{m.label}</div>
                {loading ? (
                  <Skeleton className="h-9 w-20 mt-2" />
                ) : (
                  <div className="text-3xl font-outfit font-semibold mt-2 tracking-tight">{m.value}</div>
                )}
                <div className="text-xs text-slate-500 mt-1">{m.sub}</div>
              </div>
              <div className={`w-9 h-9 rounded-md flex items-center justify-center ${m.iconBg}`}>
                <m.icon className="w-4 h-4" strokeWidth={2} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Card className="lg:col-span-2 p-6 border-slate-200 shadow-none">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit text-lg font-semibold">Documentos por Categoria</h2>
          </div>
          <div className="h-64">
            <Suspense fallback={<Skeleton className="w-full h-full" />}>
              <CategoryChart data={chartData} />
            </Suspense>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-none">
          <h2 className="font-outfit text-lg font-semibold mb-4">Status</h2>
          <div className="space-y-3">
            {statusData.map((s) => {
              const total = statusData.reduce((acc, x) => acc + x.value, 0) || 1;
              const pct = Math.round((s.value / total) * 100);
              return (
                <div key={s.name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-700">{s.name}</span>
                    <span className="font-medium tabular-nums">{s.value}</span>
                  </div>
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: s.fill }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Vistorias de Hoje */}
      <Suspense fallback={<Skeleton className="h-32 w-full mb-6" />}>
        <RemindersCard />
      </Suspense>

      <Card className="p-6 border-slate-200 shadow-none mb-6" data-testid="today-inspections-card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-outfit text-lg font-semibold flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" /> Vistorias de Hoje
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {loadingInspections
                ? "Carregando..."
                : inspections
                  ? `${inspections.done_count}/${inspections.total_employees} colaborador(es) já enviaram checklist hoje`
                  : "—"}
            </p>
          </div>
          <Link to="/checklist">
            <Button variant="outline" size="sm" data-testid="go-to-checklists-btn" className="h-8">
              Ver detalhes
            </Button>
          </Link>
        </div>

        {loadingInspections ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-md border border-emerald-100 bg-emerald-50/30" data-testid="inspections-done-panel">
              <div className="px-4 py-2 border-b border-emerald-100 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                  <CheckCircle2 className="w-4 h-4" /> Concluídas
                </div>
                <Badge variant="outline" className="text-[11px] bg-white">
                  {inspections?.done_count ?? 0}
                </Badge>
              </div>
              {(!inspections?.done || inspections.done.length === 0) ? (
                <div className="p-6 text-xs text-slate-500 text-center">Nenhuma vistoria enviada hoje.</div>
              ) : (
                <ul className="divide-y divide-emerald-100/70">
                  {inspections.done.map((d) => (
                    <li key={d.id} className="px-4 py-2.5 flex items-center justify-between text-sm" data-testid={`inspection-done-${d.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <User className="w-3.5 h-3.5 text-emerald-700" />
                        <span className="font-medium truncate">{d.name}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 text-xs text-slate-600">
                        <span className="tabular-nums">{d.plate}</span>
                        <span className="tabular-nums">{d.time_brt}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-md border border-amber-100 bg-amber-50/30" data-testid="inspections-pending-panel">
              <div className="px-4 py-2 border-b border-amber-100 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                  <XCircle className="w-4 h-4" /> Pendentes
                </div>
                <Badge variant="outline" className="text-[11px] bg-white">
                  {inspections?.pending_count ?? 0}
                </Badge>
              </div>
              {(!inspections?.pending || inspections.pending.length === 0) ? (
                <div className="p-6 text-xs text-slate-500 text-center">Todos enviaram — tudo em dia.</div>
              ) : (
                <ul className="divide-y divide-amber-100/70">
                  {inspections.pending.map((p) => (
                    <li key={p.id} className="px-4 py-2.5 flex items-center gap-2 text-sm" data-testid={`inspection-pending-${p.id}`}>
                      <User className="w-3.5 h-3.5 text-amber-700" />
                      <span className="truncate">{p.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Expiring */}
      <Card className="p-6 border-slate-200 shadow-none">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-outfit text-lg font-semibold">Documentos a vencer ou vencidos</h2>
            <p className="text-xs text-slate-500 mt-1">Itens que requerem atenção</p>
          </div>
        </div>

        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : stats?.expiring_items?.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm" data-testid="no-expiring-items">
            Nenhum documento próximo do vencimento. 
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="expiring-items-list">
            {stats?.expiring_items?.map((item) => {
              const s = getExpirationStatus(item.expiration_date);
              const resolveDoc = async (e) => {
                e.preventDefault(); e.stopPropagation();
                try {
                  await api.post(`/documents/${item.id}/resolve-expiration`);
                  toast.success("Documento marcado como resolvido");
                  setStats((cur) => {
                    if (!cur) return cur;
                    const key = item.status; // "vencido" or "vence_em_breve"
                    const prevStatus = cur.stats_by_status || {};
                    const nextStatus = {
                      ...prevStatus,
                      [key]: Math.max(0, (prevStatus[key] || 0) - 1),
                      valido: (prevStatus.valido || 0) + 1,
                    };
                    return {
                      ...cur,
                      expiring_items: (cur.expiring_items || []).filter((x) => x.id !== item.id),
                      stats_by_status: nextStatus,
                    };
                  });
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Falha ao resolver");
                }
              };
              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between py-3 hover:bg-slate-50 px-2 -mx-2 rounded-md transition-colors"
                  data-testid={`expiring-item-${item.id}`}
                >
                  <Link to={`/colaboradores/${item.employee_id}`} className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-slate-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{item.title}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {item.employee_name} · {CATEGORY_LABELS[item.category]}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm text-slate-600 hidden sm:inline">{formatDate(item.expiration_date)}</span>
                    <Badge className={`${s.classes} border rounded-full px-2 py-0.5 text-xs font-semibold`}>
                      {s.label}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={resolveDoc}
                      data-testid={`resolve-expired-${item.id}`}
                      className="h-7 px-2 text-[11px] text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      title="Marcar como resolvido"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolvido
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
